import { store } from '@/vuex/store'
import tokenService from './tokenService'

// Refresh buffer: refresh token 5 minutes before expiration
const EXPIRATION_BUFFER_MS = 5 * 60 * 1000;

// Minimum delay between refresh attempts (prevents rapid retries on failure)
const MIN_REFRESH_DELAY_MS = 30 * 1000;

// Fallback check interval if we can't determine token expiration
const FALLBACK_CHECK_INTERVAL_MS = 15 * 60 * 1000;

let refreshTimeoutId = null;

/**
 * Format milliseconds to human readable string (e.g., "5m 30s")
 */
const formatMs = (ms) => {
    if (ms <= 0) return '0s';
    const seconds = Math.floor(ms / 1000) % 60;
    const minutes = Math.floor(ms / 1000 / 60) % 60;
    const hours = Math.floor(ms / 1000 / 60 / 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

/**
 * Get current time formatted as HH:MM:SS
 */
const getTimestamp = () => {
    return new Date().toLocaleTimeString('en-GB', { hour12: false });
};

/**
 * Get token expiration time formatted
 */
const getTokenExpiryTime = (token) => {
    if (!token) return 'N/A';
    const payload = tokenService.parseJwt(token);
    if (!payload || !payload.exp) return 'N/A';
    return new Date(payload.exp * 1000).toLocaleTimeString('en-GB', { hour12: false });
};

/**
 * Start smart token refresh - schedules refresh based on actual token expiration
 */
const refreshPeriodicallyToken = async () => {
    // Clear any existing timeout
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }

    console.log(`%c[TokenReset] ${getTimestamp()} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'color: #4CAF50; font-weight: bold');
    console.log(`%c[TokenReset] ${getTimestamp()} 🚀 Starting smart token refresh manager`, 'color: #4CAF50');

    // Initial token check and schedule next refresh
    await checkAndRefreshToken();
    scheduleNextRefresh();
};

/**
 * Schedule the next token refresh based on current token's expiration
 */
const scheduleNextRefresh = () => {
    // Clear any existing timeout
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }

    const currentToken = store.state.aToken;
    let delay;
    let reason;

    if (currentToken) {
        const timeRemaining = tokenService.getTokenTimeRemaining(currentToken);
        // Refresh 5 minutes before expiration
        delay = Math.max(timeRemaining - EXPIRATION_BUFFER_MS, MIN_REFRESH_DELAY_MS);
        reason = delay === MIN_REFRESH_DELAY_MS ? 'minimum delay' : 'token expiry - 5min buffer';
    } else {
        // No token, use fallback interval
        delay = FALLBACK_CHECK_INTERVAL_MS;
        reason = 'no token (fallback)';
    }

    const nextRefreshTime = new Date(Date.now() + delay).toLocaleTimeString('en-GB', { hour12: false });

    console.log(`%c[TokenReset] ${getTimestamp()} ⏰ Next refresh scheduled:`, 'color: #2196F3');
    console.log(`%c[TokenReset] ${getTimestamp()}    ├─ Delay: ${formatMs(delay)}`, 'color: #2196F3');
    console.log(`%c[TokenReset] ${getTimestamp()}    ├─ Will refresh at: ${nextRefreshTime}`, 'color: #2196F3');
    console.log(`%c[TokenReset] ${getTimestamp()}    └─ Reason: ${reason}`, 'color: #2196F3');

    refreshTimeoutId = setTimeout(async () => {
        console.log(`%c[TokenReset] ${getTimestamp()} ⏰ Scheduled refresh triggered`, 'color: #FF9800; font-weight: bold');
        await checkAndRefreshToken();
        scheduleNextRefresh();
    }, delay);
};

/**
 * Check token validity and refresh if needed
 */
const checkAndRefreshToken = async () => {
    try {
        const currentToken = store.state.aToken;

        console.log(`%c[TokenReset] ${getTimestamp()} 🔍 Checking token status...`, 'color: #9C27B0');

        if (!currentToken) {
            console.log(`%c[TokenReset] ${getTimestamp()} ⚠️ No token found, acquiring new token...`, 'color: #FF5722');
            await tokenService.refreshToken();
            const newToken = store.state.aToken;
            console.log(`%c[TokenReset] ${getTimestamp()} ✅ Token acquired, expires at: ${getTokenExpiryTime(newToken)}`, 'color: #4CAF50');
            return;
        }

        const timeRemaining = tokenService.getTokenTimeRemaining(currentToken);
        const expiryTime = getTokenExpiryTime(currentToken);

        // Only refresh if token is expiring within the buffer window
        if (timeRemaining <= EXPIRATION_BUFFER_MS) {
            console.log(`%c[TokenReset] ${getTimestamp()} 🔄 Token expiring soon, refreshing...`, 'color: #FF9800; font-weight: bold');
            console.log(`%c[TokenReset] ${getTimestamp()}    ├─ Time remaining: ${formatMs(timeRemaining)}`, 'color: #FF9800');
            console.log(`%c[TokenReset] ${getTimestamp()}    └─ Expires at: ${expiryTime}`, 'color: #FF9800');

            await tokenService.refreshToken();

            const newToken = store.state.aToken;
            const newTimeRemaining = tokenService.getTokenTimeRemaining(newToken);
            console.log(`%c[TokenReset] ${getTimestamp()} ✅ Token refreshed successfully!`, 'color: #4CAF50; font-weight: bold');
            console.log(`%c[TokenReset] ${getTimestamp()}    ├─ New expiry: ${getTokenExpiryTime(newToken)}`, 'color: #4CAF50');
            console.log(`%c[TokenReset] ${getTimestamp()}    └─ Valid for: ${formatMs(newTimeRemaining)}`, 'color: #4CAF50');
        } else {
            console.log(`%c[TokenReset] ${getTimestamp()} ✓ Token is valid`, 'color: #8BC34A');
            console.log(`%c[TokenReset] ${getTimestamp()}    ├─ Time remaining: ${formatMs(timeRemaining)}`, 'color: #8BC34A');
            console.log(`%c[TokenReset] ${getTimestamp()}    └─ Expires at: ${expiryTime}`, 'color: #8BC34A');
        }
    } catch (error) {
        console.error(`%c[TokenReset] ${getTimestamp()} ❌ Token refresh failed!`, 'color: #F44336; font-weight: bold');
        console.error(`[TokenReset] ${getTimestamp()} Error:`, error);

        // Dispatch error event for the app to handle
        window.dispatchEvent(new CustomEvent('auth-error', {
            detail: {
                error: error,
                message: 'Session refresh failed. You may need to log in again.'
            }
        }));
    }
};

/**
 * Stop token refresh scheduling
 */
const stopTokenRefresh = () => {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
        console.log(`%c[TokenReset] ${getTimestamp()} 🛑 Token refresh manager stopped`, 'color: #F44336');
    }
};

/**
 * Force immediate token refresh and reschedule
 */
const forceRefresh = async () => {
    console.log(`%c[TokenReset] ${getTimestamp()} ⚡ Force refresh requested`, 'color: #E91E63; font-weight: bold');
    const token = await tokenService.refreshToken();
    const newTimeRemaining = tokenService.getTokenTimeRemaining(token);
    console.log(`%c[TokenReset] ${getTimestamp()} ✅ Force refresh complete`, 'color: #4CAF50');
    console.log(`%c[TokenReset] ${getTimestamp()}    └─ Valid for: ${formatMs(newTimeRemaining)}`, 'color: #4CAF50');
    scheduleNextRefresh();
    return token;
};

export { refreshPeriodicallyToken, stopTokenRefresh, forceRefresh, checkAndRefreshToken }
